/**
 * TA 的朋友（新版，UF-01「宠物交流」）：把 GET /pets/{pet_id}/friends（PetFriendPage）与
 * GET /pets/{pet_id}/friends/{friend_id}（PetFriendDetail）变成要显示的文字（纯函数，便于单独测试）。
 *
 * 口径（I 冻结的 UF01-FRIEND-DETAIL-F0，并入 c84a 1402Z 对五问的裁决）：
 * - 有没有来回只按真送到的话分：聊过 / 打过招呼 / 没接上 / 遇见过；null 是不知道，不写；
 * - 关系只把 ended 写成“已不是朋友”；friends 不代表聊过，不写成“是朋友”；none、null 都不写；
 * - 一场聊天：invited 说“等回应”；还没有送到的话、还没结束说“联系中”，不说“正在聊”；
 *   收尾 chatted 说“结束了”，不说“聊完了”（收尾不代表有过来回）；
 * - 关系不是位置：地点只说“在哪遇见过”，不说此刻在哪；
 * - 每一段为 null 是不知道（页面不显示这一段），为空是没有（说一句“还没有……”）；
 * - 不认识的取值（后端以后新增）一律不显示，原始值不上页面；名字、地点为空时有退回的说法。
 * 宠物自己做主：这里只有看，没有替宠物加、删、批准朋友的动作。
 */
import type {
  PetChatEnding,
  PetChatExchange,
  PetChatKind,
  PetChatStatus,
  PetFriendChat,
  PetFriendEncounters,
  PetFriendEpisode,
  PetFriendExchange,
  PetFriendHistory,
  PetFriendRelation,
  PetFriendSummary,
} from "@/shared/contracts";
import type { ChipTone } from "@/shared/ui";
import { serverNow } from "@/shared/time/clock";
import { speciesLabel } from "./friends";

/** 数字和它的单位之间用不断行空格：窄屏不会折成“3 / 次”。 */
const NB = "\u00a0";

/** 按表查：只认表里自己的键（“constructor”这类取值不会碰巧查到原型上的东西）；查不到就是 null，不回显原始值。 */
function pick<K extends string, V>(table: Record<K, V>, raw: string | null | undefined): V | null {
  if (typeof raw !== "string" || !Object.prototype.hasOwnProperty.call(table, raw)) return null;
  return table[raw as K];
}

/* ---------------- 一位朋友 ---------------- */

/** 有没有来回（取最好的一场）。用 Record 写满全部取值：契约多一个取值时这里编译不过，不会悄悄漏翻。 */
const FRIEND_EXCHANGE: Record<PetFriendExchange, { label: string; tone: ChipTone }> = {
  both: { label: "聊过", tone: "leaf" },
  one_way: { label: "打过招呼", tone: "sky" },
  none: { label: "没接上", tone: "neutral" },
  met_only: { label: "遇见过", tone: "neutral" },
};

export function friendExchangeOf(raw: string | null | undefined): { label: string; tone: ChipTone } | null {
  return pick(FRIEND_EXCHANGE, raw);
}

/** 关系：只有“已不是朋友”要写出来；friends（不代表聊过）、none、null 都不写（c84a D1402 P5）。 */
const RELATION: Record<PetFriendRelation, string | null> = {
  friends: null,
  ended: "已不是朋友",
  none: null,
};

export function relationLabel(raw: string | null | undefined): string | null {
  return pick(RELATION, raw);
}

/** 名字：后端总会给；万一是空的，用“这位朋友”，不留空白。 */
export function friendName(raw: string | null | undefined): string {
  const name = typeof raw === "string" ? raw.trim() : "";
  return name || "这位朋友";
}

/** 宠物还是星球居民：宠物写“宠物 · 猫”（物种认不出只写“宠物”）；星球居民另用标签标出，这里不写；不认识的种类什么都不写。 */
export function kindText(kind: string | null | undefined, species: string | null | undefined): string | null {
  if (kind !== "pet") return null;
  const name = speciesLabel(species);
  return name ? `宠物 · ${name}` : "宠物";
}

export const isResident = (kind: string | null | undefined): boolean => kind === "resident";

/** 有效的次数（≥1 的有限数，取整）；0、null、读不出都返回 null。 */
function countOf(raw: number | null | undefined): number | null {
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : null;
}

/*
 * 两种次数的说法（c84a D1736-4 采用 2531 R25 写法 A）：旧的相遇记录叫“同地”（同一时间在同一个地方），现场相遇叫“当面”
 * （面对面遇上，聊没聊起来都算）。两个数分开写、不相加、不写“其中”，也不据次数推“熟人 / 好朋友”；
 * null 是不知道，不写成 0；列表上 0 次不写；星球居民没有当面这一项。
 */

/** 旧记录里同地的次数（null 表示旧记录里没有这一位，不是 0 次）。 */
export function historyCountText(raw: number | null | undefined): string | null {
  const n = countOf(raw);
  return n === null ? null : `同地${NB}${n}${NB}次`;
}

/** 现场当面的次数（与上面那个分开给、不相加）。 */
export function encounterCountText(raw: number | null | undefined): string | null {
  const n = countOf(raw);
  return n === null ? null : `当面${NB}${n}${NB}次`;
}

/** 读屏念的完整说法：“同一时间在同一个地方 3 次，面对面遇上 1 次”（没有的那一半不念）。 */
export function countsSpoken(history: number | null | undefined, encounters: number | null | undefined): string | null {
  const h = countOf(history);
  const e = countOf(encounters);
  const parts = [h === null ? null : `同一时间在同一个地方 ${h} 次`, e === null ? null : `面对面遇上 ${e} 次`].filter(Boolean);
  return parts.length ? parts.join("，") : null;
}

/** 日期：今年的写“9月30日”，往年的带上年份；没有或读不出来返回 null。按本机时区。 */
export function dayText(iso: string | null | undefined, now: Date = new Date(serverNow())): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return null;
  const day = `${at.getMonth() + 1}月${at.getDate()}日`;
  return at.getFullYear() === now.getFullYear() ? day : `${at.getFullYear()}年${day}`;
}

/** “最近往来 9月30日”：读不出日期就不写。 */
export function lastInteractionText(iso: string | null | undefined, now?: Date): string | null {
  const day = dayText(iso, now);
  return day ? `最近往来 ${day}` : null;
}

/** 地点：去掉前后空白；空的返回 null。 */
export function placeOf(raw: string | null | undefined): string | null {
  const place = typeof raw === "string" ? raw.trim() : "";
  return place || null;
}

/** 列表里一位朋友要显示的东西。 */
export interface FriendRowView {
  id: string;
  name: string;
  resident: boolean;
  exchange: { label: string; tone: ChipTone } | null;
  relation: string | null;
  /** 宠物 · 猫（星球居民另用标签，不写） */
  facts: string[];
  /** 同地 N 次 · 当面 N 次（单独一行；没有的那一半不写，两半都没有就是 null） */
  counts: string | null;
  /** 最近往来 9月30日 */
  when: string | null;
  /** 读屏念的一整句：名字在前，再是分类、关系、事实、次数（完整说法）、最近往来。 */
  spoken: string;
}

export function friendRow(friend: PetFriendSummary, now?: Date): FriendRowView {
  const name = friendName(friend.name);
  const exchange = friendExchangeOf(friend.exchange);
  const relation = relationLabel(friend.relation);
  const resident = isResident(friend.kind);
  const facts = [kindText(friend.kind, friend.species)].filter((text): text is string => Boolean(text));
  // 星球居民没有当面这一项（固定是 0，写出来像是“还没遇上”）
  const encounters = resident ? null : friend.encounter_count;
  const counts = [historyCountText(friend.meet_count_history), encounterCountText(encounters)].filter(Boolean).join(" · ") || null;
  const when = lastInteractionText(friend.last_interaction_at, now);
  const spoken = [exchange?.label, relation, resident ? "星球居民" : null, ...facts, countsSpoken(friend.meet_count_history, encounters), when].filter(Boolean).join("，");
  return { id: friend.friend_id, name, resident, exchange, relation, facts, counts, when, spoken: spoken ? `${name}：${spoken}` : name };
}

/* ---------------- 相遇 ---------------- */

export interface MeetingView {
  /** 同地 N 次 / 当面 N 次 / 当面：还没有 */
  headline: string;
  /** 这个数是什么：同一时间在同一个地方 / 面对面遇上，聊没聊起来都算（“当面：还没有”时不写） */
  note: string | null;
  /** 第一次：9月20日（和最近一次同一天时不写） */
  first: string | null;
  /** 同地：最近一次：9月30日，在西湖；当面：最近当面：9月30日，在西湖 */
  last: string | null;
}

const HISTORY_NOTE = "同一时间在同一个地方";
const ENCOUNTER_NOTE = "面对面遇上，聊没聊起来都算";

function firstAndLast(lead: string, firstIso: string | null | undefined, lastIso: string | null | undefined, lastPlace: string | null | undefined, now?: Date) {
  const firstDay = dayText(firstIso, now);
  const lastDay = dayText(lastIso, now);
  const place = placeOf(lastPlace);
  const last = lastDay ? `${lead}：${lastDay}${place ? `，在${place}` : ""}` : place ? `${lead}：在${place}` : null;
  const first = firstDay && firstDay !== lastDay ? `第一次：${firstDay}` : null;
  return { first, last };
}

/** 旧的相遇记录（同地）：同一时刻、同一个地方才记一次，只说明遇见过。次数读不出来、或 0 次时整段不写（返回 null）。 */
export function historyView(history: PetFriendHistory | null | undefined, now?: Date): MeetingView | null {
  const n = countOf(history?.count);
  if (!history || n === null) return null;
  return { headline: `同地${NB}${n}${NB}次`, note: HISTORY_NOTE, ...firstAndLast("最近一次", history.first_met_at, history.last_met_at, history.last_place, now) };
}

/** 同地过的地方：去空白、去重，最多 10 个（与后端同一个上限）。 */
export function placesOf(history: PetFriendHistory | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of history?.places ?? []) {
    const place = placeOf(raw);
    if (place && !out.includes(place)) out.push(place);
    if (out.length === 10) break;
  }
  return out;
}

/**
 * 现场相遇（当面）：一次就是一场见面的会话，不管聊没聊起来。0 次写“当面：还没有”；次数读不出来（不知道）整项不写。
 * 星球居民没有当面这一项（固定是 0，写出来像是“还没遇上”）：resident 为 true 时不写。
 * 地点是 TA 自己去的那个地方，写成“最近当面：……”，和同地那一项分开。
 */
export function encountersView(encounters: PetFriendEncounters | null | undefined, resident = false, now?: Date): MeetingView | null {
  if (!encounters || resident) return null;
  const raw = encounters.count;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;
  const n = countOf(raw);
  if (n === null) return { headline: "当面：还没有", note: null, first: null, last: null };
  return { headline: `当面${NB}${n}${NB}次`, note: ENCOUNTER_NOTE, ...firstAndLast("最近当面", encounters.first_at, encounters.last_at, encounters.last_place, now) };
}

/* ---------------- 一起经历的事 ---------------- */

/** 一段共同经历：哪天、在哪（不知道就不写）、说上话没有。不给原话。 */
export function episodeText(episode: PetFriendEpisode, now?: Date): string {
  return [dayText(episode.happened_at, now), placeOf(episode.place_label) ? `在${placeOf(episode.place_label)}` : null, episode.talked ? "说上了话" : "没说上话"]
    .filter(Boolean)
    .join(" · ");
}

/* ---------------- 一场聊天 ---------------- */

const CHAT_KIND: Record<PetChatKind, string> = {
  live: "见面",
  post: "消息",
};

/** 这一场是见面时聊的，还是 TA 回家后发的消息。 */
export function chatKindLabel(raw: string | null | undefined): string | null {
  return pick(CHAT_KIND, raw);
}

const CHAT_EXCHANGE: Record<PetChatExchange, { label: string; tone: ChipTone }> = {
  both: { label: "聊过", tone: "leaf" },
  one_way: { label: "打过招呼", tone: "sky" },
  none: { label: "没接上", tone: "neutral" },
};

/** 还没有分类（谁的话都还没送到）时按状态说：等回应、联系中；不说“正在聊”。已经结束的交给收尾那一行。 */
const CHAT_STATUS: Record<PetChatStatus, { label: string; tone: ChipTone } | null> = {
  invited: { label: "等回应", tone: "neutral" },
  active: { label: "联系中", tone: "neutral" },
  ended: null,
};

/** 一场聊天的分类那一格：有分类用分类，没有分类按状态。 */
export function chatStateOf(chat: { status: string; exchange?: string | null }): { label: string; tone: ChipTone } | null {
  return pick(CHAT_EXCHANGE, chat.exchange) ?? pick(CHAT_STATUS, chat.status);
}

const CHAT_ENDING: Record<PetChatEnding, string> = {
  chatted: "结束了",
  no_reply: "没接上话",
  stopped: "中途停下",
  expired: "已过期",
};

/**
 * 收尾那一行小字（只在结束了的场次）：结束了 / 没接上话 / 中途停下 / 已过期。
 * 分类已经是“没接上”、收尾又是“没接上话”时不重复写。
 */
export function chatEndingLabel(chat: { status: string; ended_how?: string | null; exchange?: string | null }): string | null {
  if (chat.status !== "ended") return null;
  if (chat.exchange === "none" && chat.ended_how === "no_reply") return null;
  return pick(CHAT_ENDING, chat.ended_how);
}

/** 句数：只数真送到的；两边都是 0 不写。 */
export function chatCountsText(chat: Pick<PetFriendChat, "mine_delivered" | "theirs_delivered">): string | null {
  const mine = countOf(chat.mine_delivered);
  const theirs = countOf(chat.theirs_delivered);
  const parts = [mine === null ? null : `送到对方${NB}${mine}${NB}句`, theirs === null ? null : `收到对方${NB}${theirs}${NB}句`].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** 一场聊天的标题：“见面 · 9月30日”。 */
export function chatTitle(chat: { kind: string; started_at: string | null }, now?: Date): string {
  return [chatKindLabel(chat.kind), dayText(chat.started_at, now)].filter(Boolean).join(" · ") || "一场聊天";
}

/* ---------------- 查询键与去处 ---------------- */

/**
 * 查询键：挂在 "social" 前缀下（设置页改动后按 ["social"] 失效时一起重读），按账号 + 宠物分开；切宠物时随按宠物区分的结果一起清掉。
 * 先放在模块里（写法同 ./events 的 planetEventsKey）；shared 的 queryKeys 开放后再登记过去。
 */
export const petFriendsKey = (userId: string | null, petId: string | null) => ["social", "pet-friends", userId ?? "-", petId ?? "-"] as const;
export const petFriendKey = (userId: string | null, petId: string | null, friendId: string | null) =>
  ["social", "pet-friend", userId ?? "-", petId ?? "-", friendId ?? "-"] as const;

/** 朋友列表在通讯器的“TA 的朋友”分区。 */
export const FRIENDS_LIST_HREF = "/communicator?channel=friends";

/** 朋友详情的去处（通讯器模块挂的二级页）；编号做 URL 编码，空编号不给链接。 */
export function friendDetailHref(friendId: string | null | undefined): string | null {
  return friendId ? `/communicator/friends/${encodeURIComponent(friendId)}` : null;
}
